<template>
    <div :class="{'nps-validation-error': hasError}" class="nps-container">
        <!-- NPS Rating Section -->
        <div class="nps-main-layout">
            <!-- Labels Section - Improved Display -->
            <div class="labels-section" v-if="increaseDecrease">
                <div class="label-item label-left">
                    <v-textarea v-model="unlikeTextFieldValue"
                                  :label="unlikeTextFieldLabel"
                                  hide-details
                                  variant="outlined"
                                  density="compact"
                                  color="#0a716e"
                                  class="nps-text-field"
                                  @click="clearDefaultText('unlikeTextFieldValue')"
                                  @input="$emit('unlikeTextFieldChange', unlikeTextFieldValue)"></v-textarea>
                </div>

                <div class="label-item label-right">
                    <v-textarea v-model="likeTextFieldValue"
                                  :label="likeTextFieldLabel"
                                  hide-details
                                  variant="outlined"
                                  density="compact"
                                  color="#0a716e"
                                  class="nps-text-field"
                                  @click="clearDefaultText('likeTextFieldValue')"
                                  @input="$emit('likeTextFieldChange', likeTextFieldValue)"></v-textarea>
                </div>
            </div>

            <!-- NPS Circles - Enhanced Design -->
            <div class="circles-wrapper">
                <div class="circles-container">
                    <div class="circles-inner">
                        <div v-for="(circle, index) in circles"
                             :key="index"
                             class="circle-item">
                            <div :class="circleClasses(index)"
                                 @click="changeCircleColor(index)"
                                 @mouseenter="hoveredIndex = index"
                                 @mouseleave="hoveredIndex = -1">
                                <span class="circle-number">{{ index }}</span>
                                
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Scale indicators -->
                <div class="scale-indicators">
                    <span class="scale-label scale-start" v-html="unlikeTextFieldValue.replace('\n','<br/>') || '0'"></span>
                    <span class="scale-label scale-end" v-html="likeTextFieldValue.replace('\n','<br/>') || (circles.length - 1)"></span>
                </div>
            </div>
        </div>

        <!-- Add/Remove controls - Enhanced Design -->
        <div v-if="increaseDecrease" class="controls-container">
            <div class="controls-wrapper">
                <v-btn variant="outlined"
                       color="#0a716e"
                       @click="removeCircle"
                       :disabled="circles.length <= 4"
                       class="control-btn"
                       size="small">
                    <v-icon>mdi-minus</v-icon>
                    <span class="btn-text">{{$t('Remove')}}</span>
                </v-btn>
                <span class="controls-label">{{$t('ScaleOptions')}} ({{ circles.length }})</span>
                <v-btn variant="outlined"
                       color="#0a716e"
                       @click="addCircle"
                       :disabled="circles.length >= 13"
                       class="control-btn"
                       size="small">
                    <v-icon>mdi-plus</v-icon>
                    <span class="btn-text">{{$t('Add')}}</span>
                </v-btn>
            </div>
        </div>
        <div v-if="hasError" class="nps-error-message">
            {{errorMessage}}
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            likeTextFieldLabel: String,
            unlikeTextFieldLabel: String,
            clickedIndex: Number,
            initialUnlikeTextFieldValue: String,
            initialLikeTextFieldValue: String,
            increaseDecrease: Boolean,
            defaultValue: {
                type: String,
                default: '##11'
            },
            value: String,
            error: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: 'This field is required'
            }
        },
        data() {
            return {
                circles: [],
                selectedIndex: -1,
                hoveredIndex: -1,
                unlikeTextFieldValue: '',
                likeTextFieldValue: ''
            }
        },
        mounted() {

            if (this.value == undefined) {
                const [likeTextFieldValue, unlikeTextFieldValue, circlesLength] = this.defaultValue.split('#')
                this.unlikeTextFieldValue = unlikeTextFieldValue || ''
                this.likeTextFieldValue = likeTextFieldValue || ''
                if (circlesLength) {
                    this.updateCircles(parseInt(circlesLength))
                    this.selectedIndex = -1
                }
            }

        },
        watch: {
            value: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (!isNaN(newVal)) {
                        this.changeCircleColor(parseInt(newVal))
                    }
                    else if (newVal && newVal.includes('#')) {

                        const [likeTextFieldValue, unlikeTextFieldValue, circlesLength] = newVal.split('#')
                        this.unlikeTextFieldValue = unlikeTextFieldValue || ''
                        this.likeTextFieldValue = likeTextFieldValue || ''

                        if (circlesLength) {
                            this.updateCircles(parseInt(circlesLength))
                            this.selectedIndex = -1
                        }
                    } else {
                        this.localValue = '##11'
                    }
                }
            },
            likeTextFieldValue(newValue) {
                this.$emit('input', `${newValue}#${this.unlikeTextFieldValue}#${this.circlesLength}`)
            },
            unlikeTextFieldValue(newValue) {
                this.$emit('input', `${this.likeTextFieldValue}#${newValue}#${this.circlesLength}`)
            },
            circles: {
                deep: true,
                handler(newCircles, oldCircles) {
                    this.$emit('input', `${this.likeTextFieldValue}#${this.unlikeTextFieldValue}#${this.circlesLength}`)
                }
            }
        },
        methods: {
            getLabel(val) {

                if (val === 'unlikeTextFieldValue') {
                    return this.unlikeTextFieldValue
                } else {
                    return this.likeTextFieldValue
                }
            },
            updateCircles(newLength) {

                if (newLength > this.circles.length) {
                    while (this.circles.length < newLength) {
                        this.circles.push(null)
                    }
                } else if (newLength < this.circles.length) {
                    this.circles.splice(newLength)
                }
            },
            changeCircleColor(index) {
                this.selectedIndex = index
                if (!this.increaseDecrease)
                    this.$emit('input', index.toString())
            },
            getIcon(index) {
                if (index < 7) {
                    return 'mdi-emoticon-sad'
                } else if (index >= 7 && index < 9) {
                    return 'mdi-emoticon-neutral'
                } else {
                    return 'mdi-emoticon-excited'
                }
            },
            clearDefaultText(fieldName) {
                if (this[fieldName] === '' || this[fieldName] === '') {
                    this[fieldName] = ''
                }
            },
            addCircle() {
                if (this.circles.length < 13) {
                    this.circles.push(null)
                }
            },
            removeCircle() {
                if (this.circles.length > 4) {
                    this.circles.pop()
                    if (this.selectedIndex >= this.circles.length) {
                        this.selectedIndex = this.circles.length - 1
                    }
                }
            },
            circleClasses(index) {
                return {
                    'circle': true,
                    'activeCircle': this.selectedIndex === index,
                    'redCircle': this.selectedIndex === index && index < Math.floor(this.circles.length * 0.7),
                    'yellowCircle': this.selectedIndex === index && index >= Math.floor(this.circles.length * 0.7) && index < Math.floor(this.circles.length * 0.9),
                    'blueCircle': this.selectedIndex === index && index >= Math.floor(this.circles.length * 0.9)
                }
            },
            iconClasses(index) {
                return {
                    'redCircleIcon': this.selectedIndex === index && index < Math.floor(this.circles.length * 0.6),
                    'yellowCircleIcon': this.selectedIndex === index && index >= Math.floor(this.circles.length * 0.6) && index < Math.floor(this.circles.length * 0.8),
                    'blueCircleIcon': this.selectedIndex === index && index >= Math.floor(this.circles.length * 0.8)
                }
            }
        },
        computed: {
            circlesLength() {
                return this.circles.length
            },
            showThumb() {
                return this.selectedIndex !== -1
            },
            hasError() {
                return this.error && this.selectedIndex === -1
            }
        }
    }
</script>

<style scoped>
    /* Main container - Modern styling */
    .nps-container {
        margin-top: 30px;
        width: 100%;
        padding: 20px;
        background: linear-gradient(135deg, #f5f7fa 0%, #f0f3f7 100%);
        border-radius: 16px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .nps-validation-error {
        border: 2px solid #ff5252;
        animation: shake 0.5s;
    }

    @keyframes shake {
        0%, 100% {
            transform: translateX(0);
        }

        10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
        }

        20%, 40%, 60%, 80% {
            transform: translateX(5px);
        }
    }

    .nps-error-message {
        color: #ff5252;
        font-size: 12px;
        margin-top: 8px;
        margin-left: 12px;
    }

    /* Main layout - responsive */
    .nps-main-layout {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 30px;
    }

    /* Labels Section - Enhanced Display */
    .labels-section {
        display: flex;
        justify-content: space-between;
        width: 100%;
        gap: 20px;
        flex-wrap: wrap;
    }

    .label-item {
        flex: 1;
        min-width: 150px;
        max-width: 250px;
    }

    .label-left {
        text-align: left;
    }

    .label-right {
        text-align: right;
    }

    /* Label Display - Attractive design */
    .label-display {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 20px;
        border-radius: 12px;
        font-weight: 600;
        font-size: 16px;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .label-negative {
        background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
        color: #d32f2f;
        border: 2px solid #ffcdd2;
    }

    .label-positive {
        background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
        color: #0a716e;
        border: 2px solid #b2ebf2;
        flex-direction: row-reverse;
    }

    .label-icon {
        font-size: 28px !important;
        opacity: 0.9;
    }

    .label-text {
        flex: 1;
        font-size: 15px;
        line-height: 1.4;
    }

    /* Text field styling */
    .nps-text-field {
        background-color: white;
        width: 100%;
        border-radius: 8px;
    }

    /* Circles wrapper */
    .circles-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    /* Circles container */
    .circles-container {
        width: 100%;
        overflow-x: auto;
        padding: 15px 10px;
        background: white;
        border-radius: 12px;
        box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
    }

        .circles-container::-webkit-scrollbar {
            height: 8px;
        }

        .circles-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
        }

        .circles-container::-webkit-scrollbar-thumb {
            background: #0a716e;
            border-radius: 4px;
        }

            .circles-container::-webkit-scrollbar-thumb:hover {
                background: #085a57;
            }

    .circles-inner {
        display: flex;
        justify-content: center;
        gap: 8px;
        min-width: fit-content;
        padding: 5px;
    }

    .circle-item {
        flex-shrink: 0;
    }

    /* Circle styling - Modern & Attractive */
    .circle {
        position: relative;
        width: 55px;
        height: 55px;
        border-radius: 50%;
        background: linear-gradient(145deg, #ffffff, #f0f0f0);
        border: 3px solid #e0e0e0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #555;
        font-weight: 700;
        font-size: 1.3em;
        cursor: pointer;
        transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
    }

        .circle:hover {
            transform: translateY(-4px) scale(1.08);
            box-shadow: 0 6px 20px rgba(10, 113, 110, 0.25);
            border-color: #0a716e;
        }

        .circle.activeCircle {
            transform: scale(1.15);
            z-index: 10;
        }

    .circle-number {
        position: relative;
        z-index: 2;
        transition: all 0.3s ease;
    }

    .circle-checkmark {
        position: absolute;
        top: -5px;
        right: -5px;
        background: white;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3;
        animation: checkPop 0.3s ease;
    }

    @keyframes checkPop {
        0% {
            transform: scale(0);
        }

        50% {
            transform: scale(1.2);
        }

        100% {
            transform: scale(1);
        }
    }

    /* Color variants - Enhanced gradients */
    .redCircle {
        background: linear-gradient(145deg, #ff5252, #e53935) !important;
        border-color: #ff1744 !important;
        color: white !important;
        box-shadow: 0 4px 15px rgba(229, 57, 53, 0.4) !important;
    }

        .redCircle:hover {
            box-shadow: 0 6px 25px rgba(229, 57, 53, 0.5) !important;
        }

    .yellowCircle {
        background: linear-gradient(145deg, #ffd54f, #ffb300) !important;
        border-color: #ffc107 !important;
        color: #5d4037 !important;
        box-shadow: 0 4px 15px rgba(255, 193, 7, 0.4) !important;
    }

        .yellowCircle:hover {
            box-shadow: 0 6px 25px rgba(255, 193, 7, 0.5) !important;
        }

    .blueCircle {
        background: linear-gradient(145deg, #0d9488, #0a716e) !important;
        border-color: #0f766e !important;
        color: white !important;
        box-shadow: 0 4px 15px rgba(10, 113, 110, 0.4) !important;
    }

        .blueCircle:hover {
            box-shadow: 0 6px 25px rgba(10, 113, 110, 0.5) !important;
        }

    .redCircleIcon {
        color: #cc0000 !important;
    }

    .yellowCircleIcon {
        color: #f9c03a !important;
    }

    .blueCircleIcon {
        color: #0a716e !important;
    }

    /* Scale indicators */
    .scale-indicators {
        display: flex;
        justify-content: space-between;
        padding: 0 10px;
    }

    .scale-label {
        font-size: 14px;
        font-weight: 600;
        color: #666;
        background: white;
        padding: 6px 16px;
        border-radius: 20px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        align-content:center;
    }

    .scale-start {
        color: #d32f2f;
        border: 2px solid #ffcdd2;
    }

    .scale-end {
        color: #0a716e;
        border: 2px solid #b2ebf2;
    }

    /* Controls container - Enhanced */
    .controls-container {
        display: flex;
        justify-content: center;
        margin-top: 20px;
        width: 100%;
    }

    .controls-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        background: white;
        padding: 12px 24px;
        border-radius: 12px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    .controls-label {
        font-size: 14px;
        font-weight: 600;
        color: #0a716e;
        padding: 0 10px;
    }

    .control-btn {
        transition: all 0.3s ease;
    }

        .control-btn:hover:not(:disabled) {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(10, 113, 110, 0.3);
        }

    .btn-text {
        margin-left: 5px;
        font-size: 13px;
    }

    /* Mobile responsive breakpoints */
    @media (max-width: 768px) {
        .circles-container{
            padding:15px 0px;
        }
        .nps-container {
            padding: 0px;
            margin-top: 20px;
        }

        .nps-main-layout {
            gap: 20px;
        }

        .labels-section {
            flex-direction: column;
            gap: 12px;
        }

        .label-item {
            max-width: 100%;
            text-align: center !important;
        }

        .label-display {
            justify-content: center;
            font-size: 14px;
            padding: 10px 16px;
        }

        .label-icon {
            font-size: 24px !important;
        }

        .circle {
            width: 25px;
            height: 25px;
            font-size: 1.1em;
        }

        .circles-inner {
            gap: 6px;
        }

        .controls-wrapper {
            padding: 10px 16px;
        }

        .btn-text {
            display: none;
        }
    }

    @media (max-width: 480px) {
        .nps-container {
            padding: 0px;
        }
        .scale-label{
            width:45%;
            align-content:center
        }
        .circle {
            width:25px;
            height: 25px;
            font-size: 1em;
            border-width: 2px;
        }

        .circles-inner {
            gap: 4px;
        }

        .label-display {
            font-size: 13px;
            padding: 8px 12px;
        }

        .scale-label {
            font-size: 12px;
            padding: 4px 12px;
        }
    }

    @media (max-width: 360px) {
        .circle {
            width: 36px;
            height: 36px;
            font-size: 0.9em;
        }

        .circles-inner {
            gap: 3px;
        }

        .circle-checkmark {
            width: 20px;
            height: 20px;
        }
    }
</style>
